import api from "@/api";
import converters from "@/converters";
import type { PlannerCategory } from "@/graphql/generated-types";
import isDataReady from "@/helpers/requests/isDataReady";
import type { LoadingType } from "@/types";
import { useAuthStore } from "@/stores/authStore";
import { computed, ref } from "vue";
import { globali18n } from "@/i18n";

const loading = ref<LoadingType>("none");
const data = ref<PlannerCategory[]>([]);

export function usePlannerCategories() {
  const { t } = globali18n;
  const authStore = useAuthStore();

  const init = async () => {
    if (loading.value === "loaded") {
      return;
    }

    loading.value = "loading";
    data.value = await api.planner.getCategories();
    loading.value = "loaded";
  };

  init();

  const categories = computed(() => data.value);

  const categoriesList = computed(() => {
    return data.value
      .map(converters.plannerCategories.toMultiSelectItem)
      .toSorted((a, b) =>
        a.title.localeCompare(b.title, authStore.institutionLocale),
      );
  });

  const categoriesOptions = computed(() => {
    return data.value
      .map(converters.plannerCategories.toOption)
      .toSorted((a, b) =>
        a.title.localeCompare(b.title, authStore.institutionLocale),
      );
  });

  const noCategoryItem = computed(() => {
    return {
      id: "",
      title: t("templates.no_category"),
      icon: "fas fa-circle",
      iconBackground: "#3B3B3B",
    };
  });

  const isReady = () => isDataReady(loading);

  const refetch = async () => {
    loading.value = "loading";
    data.value = await api.planner.getCategories();
    loading.value = "loaded";
  };

  return {
    categories,
    categoriesList,
    categoriesOptions,
    noCategoryItem,
    loading,
    refetch,
    isReady,
  };
}
